import {
  apiUrlAllProducts,
  apiUrlGetEcommerceProduct,
  apiUrlGetProduct,
  apiUrlSearchedProducts,
} from '@lib/core/products/slices/urls';
import { IApiUrlGetEcommerceProductProps, IApiUrlGetProductProps, TProductInstance } from '@lib/core/products/types';
import { TQuizAnswerTag } from '@lib/core/quizzes/types';
import {
  isQuizAnswerTagContextFilterIn,
  isQuizAnswerTagContextFilterInOverride,
  isQuizAnswerTagContextFilterProduct,
} from '@lib/core/quizzes/utils/filters';
import { ApiQueries } from '@lib/core/service/consts';
import { request } from '@lib/core/service/requests/request';
import { selectServiceProductCategory } from '@lib/core/service/selectors';
import { store } from '@lib/core/service/store';
import { IProductsResponse } from '@lib/core/service/types/interface';
import { productsSuggestionApiUrlCreator } from '@lib/core/users/slices/urls';

export interface IRequestParams {
  offset?: number;
  limit?: number;
  [ApiQueries.character]?: string;
  [ApiQueries.productCategory]?: string;
  [ApiQueries.promotions]?: string;
  [ApiQueries.preferences]?: string;
  [ApiQueries.ordering]?: string;
  [ApiQueries.associatedRetailerSlug]?: string;
  [ApiQueries.productIdentifier]?: string;
}

const getProductData = ({
  productId,
  customRetailerSlug,
  customRetailerLocationSlug,
}: IApiUrlGetProductProps): Promise<TProductInstance> => {
  const requestUrl = apiUrlGetProduct({ customRetailerLocationSlug, customRetailerSlug, productId });

  return request({
    additionalHeaders: {
      Accept: 'application/json; version="2.0"',
    },
    url: requestUrl,
  });
};

const getEcommerceProductData = ({
  url,
  customRetailerSlug,
  customRetailerLocationSlug,
}: IApiUrlGetEcommerceProductProps): Promise<TProductInstance> => {
  const requestUrl = apiUrlGetEcommerceProduct({ customRetailerLocationSlug, customRetailerSlug, url });

  return request({
    additionalHeaders: {
      Accept: 'application/json',
    },
    url: requestUrl,
  });
};

// extended
const getProductsListData = (params: IRequestParams): Promise<IProductsResponse> => {
  const paramsToBeSent = { ...params };
  if (paramsToBeSent[ApiQueries.character]) {
    paramsToBeSent[ApiQueries.character].split('-').join(',');
  }
  const requestUrl = paramsToBeSent[ApiQueries.productName] ? apiUrlSearchedProducts() : apiUrlAllProducts();
  return request({
    additionalHeaders: {
      Accept: 'application/json; version="2.0"',
    },
    params: paramsToBeSent,
    url: requestUrl,
  });
};

const getTastePathProductsData = (params: Record<string, string | number>) => {
  const state = store.getState();
  const productCategory = selectServiceProductCategory(state);

  const requestUrl = productsSuggestionApiUrlCreator({ productCategory });

  return request({
    params,
    url: requestUrl,
  });
};

/**
 * Filters and sorts products based on quiz answer tags for suggestion.
 *
 * Logic:
 * 1. Filters products excluding those with slug matching filter-product tag
 * 2. Applies filter-in-override or filter-in filter if present
 * 3. If the result has <= 1 element, adds products from the first step
 * 4. Returns maximum 2 products
 *
 * @param products - List of products to filter
 * @param answerTags - List of tags from quiz answers
 * @returns Array of recommended products (max 2)
 */

const getSuggestedProducts = (products: TProductInstance[], answerTags: TQuizAnswerTag[]) => {
  const filterProductTag = answerTags.find(isQuizAnswerTagContextFilterProduct);
  const filterInOverrideTag = answerTags.find(isQuizAnswerTagContextFilterInOverride);
  const filterInTag = answerTags.find(isQuizAnswerTagContextFilterIn);

  let resultFirstStep = [];
  let resultSecondStep = [];

  if (filterProductTag) {
    resultFirstStep = products?.filter(gprl => gprl?.product?.slug !== filterProductTag.name);
  } else {
    resultFirstStep = products;
  }

  const applyFilterIn = (tagName: string) => {
    return resultFirstStep?.filter(gprl => {
      const raw = gprl?.product?.attributes?.quiz_answer_tag_association;

      if (!raw) return false;

      let tags: string[] = [];

      try {
        tags = JSON.parse(raw.replaceAll("'", '"'));
      } catch (e) {
        console.warn('Invalid quiz_answer_tag_association JSON:', raw);
        return false;
      }

      return tags.includes(tagName);
    });
  };

  if (filterInOverrideTag) {
    resultSecondStep = applyFilterIn(filterInOverrideTag.name);
  } else if (filterInTag) {
    resultSecondStep = applyFilterIn(filterInTag.name);
  }

  if (resultSecondStep.length <= 1) {
    const merged = [
      ...resultSecondStep,
      ...resultFirstStep.filter(
        item => !resultSecondStep.some(sel => sel.product.identifier === item.product.identifier),
      ),
    ];

    return merged.slice(0, 2);
  }

  return resultSecondStep?.slice(0, 2);
};

export { getEcommerceProductData, getProductData, getProductsListData, getSuggestedProducts, getTastePathProductsData };
